<template>
  <div class="flex" :class="isOwnMessage ? 'justify-end' : 'justify-start'">
    <div class="max-w-[72%] rounded-md border px-3 py-2 shadow-sm" :class="isOwnMessage ? 'bg-primary text-primary-foreground' : 'bg-card'">
      <div class="mb-1 flex items-center justify-between gap-3 text-[11px]" :class="isOwnMessage ? 'text-primary-foreground/75' : 'text-muted-foreground'">
        <span class="flex min-w-0 items-center gap-2">
          <span class="truncate font-medium">{{ message.sender?.name || 'System' }}</span>
          <span>{{ formatTime(message.created_at) }}</span>
          <span v-if="message.edited_at">edited</span>
        </span>

        <span class="flex shrink-0 items-center gap-1">
          <span v-if="deliveryStatus" class="inline-flex h-6 w-6 items-center justify-center" :aria-label="deliveryStatusLabel" :title="deliveryStatusLabel">
            <component :is="deliveryStatusIcon" class="h-3.5 w-3.5" :class="{ 'animate-pulse': deliveryStatus === 'pending' }" />
            <span class="sr-only">{{ deliveryStatusLabel }}</span>
          </span>

          <template v-if="!isOwnMessage">
            <Button
              v-for="reaction in quickReactions"
              :key="reaction"
              type="button"
              variant="ghost"
              size="xs"
              class="h-6 w-6 px-0"
              :class="hasOwnReaction(reaction) ? 'bg-background/80 text-foreground' : ''"
              :disabled="reactingReactionName === reaction"
              :aria-label="reactionLabelFor(reaction)"
              :title="reactionLabelFor(reaction)"
              @click="toggleReaction(reaction)"
            >
              <component :is="reactionIconFor(reaction)" class="h-3.5 w-3.5" :class="hasOwnReaction(reaction) ? 'fill-current' : ''" />
            </Button>
          </template>

          <DropdownMenu v-if="isOwnMessage && isPersistedMessage">
            <DropdownMenuTrigger as-child>
              <Button type="button" variant="ghost" size="icon-sm" class="h-6 w-6" :disabled="isDeletingMessage" aria-label="Message actions" title="Message actions">
                <MoreVertical class="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-36">
              <DropdownMenuItem :disabled="isDeletingMessage" @select="startEditing">
                <Pencil />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem :disabled="isDeletingMessage" class="text-destructive focus:text-destructive" @select="confirmDeleteMessage">
                <Trash2 />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      </div>

      <form v-if="isEditing" class="flex flex-col gap-2" @submit.prevent="submitEdit">
        <Textarea v-model="editingBody" class="min-h-20 resize-none bg-background text-foreground" rows="3" @keydown.esc.prevent="cancelEditing" />
        <div class="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" @click="cancelEditing">
            Cancel
          </Button>
          <Button type="submit" size="sm" :disabled="!editingBody.trim() || isUpdatingMessage">
            Save
          </Button>
        </div>
      </form>

      <p v-if="message.body" class="whitespace-pre-wrap break-words text-sm leading-relaxed">
        {{ message.body }}
      </p>
      <p v-else-if="messageUploads.length === 0" class="break-words text-sm leading-relaxed">
        {{ attachmentLabel }}
      </p>

      <div v-if="messageUploads.length > 0" class="mt-2 flex flex-wrap gap-2">
        <UploadedFile v-for="upload in messageUploads" :key="upload.id || upload.uuid" :file="upload" :deletable="false" preview-card />
      </div>

      <div v-if="message.reactions?.length" class="mt-2 flex flex-wrap gap-1">
        <span
          v-for="reaction in message.reactions"
          :key="reaction.id"
          class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] shadow-sm"
          :class="isOwnReaction(reaction) ? 'bg-primary text-primary-foreground' : 'bg-background/80 text-foreground'"
          :title="reactionLabelFor(reaction.reaction)"
        >
          <component :is="reactionIconFor(reaction.reaction)" v-if="reactionIconFor(reaction.reaction)" class="h-3 w-3" :class="isOwnReaction(reaction) ? 'fill-current' : ''" />
          <span v-else>{{ reaction.reaction }}</span>
          <span class="sr-only">{{ reactionLabelFor(reaction.reaction) }}</span>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { AlertCircle, Check, Clock3, Eye, Heart, MoreVertical, Pencil, SendHorizontal, ThumbsUp, Trash2 } from 'lucide-vue-next'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../../../../../resources/js/components/ui/dropdown-menu/index.js'
import { Textarea } from '../../../../../../../resources/js/components/ui/textarea/index.js'
import UploadedFile from '../../../../../uploadable/resources/js/components/UploadedFile.vue'
import { useMessageReactionStore } from '../../stores/messageReactionStore.js'
import { useMessageStore } from '../../stores/messageStore.js'

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  message: {
    type: Object,
    required: true,
  },
  currentUserId: {
    type: [Number, String, null],
    default: null,
  },
  participants: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['messageActionStarted', 'messageActionFailed', 'messageUpdated', 'messageDeleted', 'messageReactionAdded', 'messageReactionRemoved'])

const messageStore = useMessageStore()
const messageReactionStore = useMessageReactionStore()

const isEditing = ref(false)
const editingBody = ref('')
const isUpdatingMessage = ref(false)
const isDeletingMessage = ref(false)
const reactingReactionName = ref(null)
const quickReactions = ['heart', 'thumbs-up']

const reactionIcons = {
  heart: Heart,
  'thumbs-up': ThumbsUp,
}

const reactionLabels = {
  heart: 'Heart',
  'thumbs-up': 'Thumbs up',
}

const deliveryStatusIcons = {
  failed: AlertCircle,
  pending: Clock3,
  sent: SendHorizontal,
  received: Check,
  seen: Eye,
}

const deliveryStatusLabels = {
  failed: 'Message failed to send',
  pending: 'Sending message',
  sent: 'Message sent to server',
  received: 'Message received',
  seen: 'Message seen',
}

const isOwnMessage = computed(() => {
  return Number(props.message.sender_user_id) === Number(props.currentUserId)
})

const numericMessageId = computed(() => Number(props.message.id))

const isPersistedMessage = computed(() => {
  return Number.isInteger(numericMessageId.value) && numericMessageId.value > 0 && !props.message.is_optimistic
})

const otherActiveParticipants = computed(() => {
  return props.participants.filter((participant) => {
    return participant.left_at === null && Number(participant.user_id) !== Number(props.currentUserId)
  })
})

const isSeenByRecipients = computed(() => {
  return isPersistedMessage.value
    && otherActiveParticipants.value.length > 0
    && otherActiveParticipants.value.every((participant) => Number(participant.last_read_message_id) >= numericMessageId.value)
})

const deliveryStatus = computed(() => {
  if (!isOwnMessage.value) {
    return null
  }

  if (props.message.delivery_status === 'failed') {
    return 'failed'
  }

  if (!isPersistedMessage.value || props.message.delivery_status === 'pending') {
    return 'pending'
  }

  if (isSeenByRecipients.value) {
    return 'seen'
  }

  return props.message.delivery_status === 'received' ? 'received' : 'sent'
})

const deliveryStatusIcon = computed(() => deliveryStatusIcons[deliveryStatus.value] || SendHorizontal)
const deliveryStatusLabel = computed(() => deliveryStatusLabels[deliveryStatus.value] || '')

const attachmentLabel = computed(() => {
  return props.message.payload?.name || props.message.payload?.uploads?.[0]?.filename || props.message.type
})

const messageUploads = computed(() => {
  if (Array.isArray(props.message.uploads)) {
    return props.message.uploads
  }

  if (Array.isArray(props.message.payload?.uploads)) {
    return props.message.payload.uploads
  }

  return []
})

const hasOwnReaction = (reactionName) => {
  return props.message.reactions?.some((reaction) => reaction.reaction === reactionName && Number(reaction.user_id) === Number(props.currentUserId)) || false
}

const isOwnReaction = (reaction) => {
  return Number(reaction.user_id) === Number(props.currentUserId)
}

const reactionIconFor = (reactionName) => {
  return reactionIcons[reactionName] || null
}

const reactionLabelFor = (reactionName) => {
  return reactionLabels[reactionName] || reactionName
}

const toggleReaction = (reactionName) => {
  if (isOwnMessage.value) {
    return
  }

  emit('messageActionStarted')

  if (hasOwnReaction(reactionName)) {
    removeReaction(reactionName)
    return
  }

  reactToMessage(reactionName)
}

const startEditing = () => {
  isEditing.value = true
  editingBody.value = props.message.body || ''
}

const cancelEditing = () => {
  isEditing.value = false
  editingBody.value = ''
}

const submitEdit = async () => {
  const body = editingBody.value.trim()

  if (!body || isUpdatingMessage.value) {
    return
  }

  emit('messageActionStarted')
  isUpdatingMessage.value = true

  try {
    const message = await messageStore.updateMessage(props.conversationId, props.message.id, { body })
    cancelEditing()
    emit('messageUpdated', message)
  } catch (error) {
    emit('messageActionFailed', errorMessageFrom(error, 'Unable to update message.'))
  } finally {
    isUpdatingMessage.value = false
  }
}

const confirmDeleteMessage = async () => {
  if (isDeletingMessage.value || !window.confirm('Delete this message?')) {
    return
  }

  emit('messageActionStarted')
  isDeletingMessage.value = true

  try {
    await messageStore.deleteMessage(props.conversationId, props.message.id)
    emit('messageDeleted', props.message)
  } catch (error) {
    emit('messageActionFailed', errorMessageFrom(error, 'Unable to delete message.'))
  } finally {
    isDeletingMessage.value = false
  }
}

const reactToMessage = async (reactionName) => {
  reactingReactionName.value = reactionName

  try {
    const reaction = await messageReactionStore.reactToMessage(props.conversationId, props.message.id, reactionName)
    emit('messageReactionAdded', { message: props.message, reaction })
  } catch (error) {
    emit('messageActionFailed', errorMessageFrom(error, 'Unable to add reaction.'))
  } finally {
    reactingReactionName.value = null
  }
}

const removeReaction = async (reactionName) => {
  reactingReactionName.value = reactionName

  try {
    await messageReactionStore.removeReaction(props.conversationId, props.message.id, reactionName)
    emit('messageReactionRemoved', { message: props.message, reaction: reactionName })
  } catch (error) {
    emit('messageActionFailed', errorMessageFrom(error, 'Unable to remove reaction.'))
  } finally {
    reactingReactionName.value = null
  }
}

const formatTime = (value) => {
  if (!value) {
    return ''
  }

  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value))
}

const errorMessageFrom = (error, fallback) => {
  const validationErrors = error?.response?.data?.errors

  if (validationErrors) {
    const messages = Object.values(validationErrors).flat().filter(Boolean)

    if (messages.length > 0) {
      return messages.join(' ')
    }
  }

  return error?.response?.data?.message || error?.message || fallback
}
</script>
